<script lang="ts">
    import { BookCopyIcon, FolderIcon, FolderOpen, PlusIcon } from "@lucide/svelte";
    import { v4 } from "uuid";
    import { language } from "../../../lang";
    import { getCurrentCharacter, getCurrentChat, type loreBook } from "../../../ts/storage/database.svelte";
    import { alertConfirm, alertMd } from "../../../ts/alert";
    import Check from "../../UI/GUI/CheckInput.svelte";
    import Help from "../../Others/Help.svelte";
    import TextInput from "../../UI/GUI/TextInput.svelte";
    import NumberInput from "../../UI/GUI/NumberInput.svelte";
    import TextAreaInput from "../../UI/GUI/TextAreaInput.svelte";
    import { tokenizeAccurate } from "src/ts/tokenizer";
    import { DBState } from "src/ts/stores.svelte";
    import LoreBookList from "./LoreBookList.svelte";
    import LoreBookStatusIcons from './LoreBookStatusIcons.svelte'
    import SolarIcon from './SolarIcon.svelte'
    import { loreBookVisualStatus } from './loreBookVisualStatus'
    import trashIcon from 'src/assets/solar-bold/trash-bin-trash-bold.svg'

    interface Props {
        value: loreBook;
        onRemove?: () => void;
        onClose?: (isDetail?: boolean) => void;
        onOpen?: (isDetail?: boolean) => void;
        idx: number;
        externalLoreBooks?: loreBook[];
        idgroup: string;
        isOpen?: boolean;
        openFolders?: number;
        isLastInContainer?: boolean;
    }

    let {
        value = $bindable(),
        onRemove = () => {},
        onClose = (isDetail = true) => {},
        onOpen = (isDetail = true) => {},
        idx,
        externalLoreBooks = $bindable(),
        idgroup,
        isOpen = false,
        openFolders = 0,
        isLastInContainer = false
    }: Props = $props();
    
    let open = $derived(isOpen)
    let visualStatus = $derived(loreBookVisualStatus(value))

    let tokens = $state(0)
    let tokenTimer: ReturnType<typeof setTimeout> | null = null
    let tokenSeq = 0
    // Re-count tokens on a debounce instead of on every content change — the
    // tokenizer runs a full CBS parse + encode, which is too heavy to do live.
    // Only when this entry is open: the token count UI renders only while open,
    // so closed entries (a big lorebook can have hundreds) must not tokenize.
    // The generation is bumped here (on content change), not in the timer, so an
    // in-flight tokenize is invalidated the moment the input changes — not only
    // once the next debounce fires 400ms later.
    $effect(() => {
        if (!open) return
        const content = value.content
        const seq = ++tokenSeq
        if (tokenTimer) clearTimeout(tokenTimer)
        tokenTimer = setTimeout(() => {
            tokenizeAccurate(content).then(result => { if (seq === tokenSeq) tokens = result })
        }, 400)
        return () => { if (tokenTimer) clearTimeout(tokenTimer) }
    })

    function isLocallyActivated(book: loreBook){
        return book.id ? getCurrentChat()?.localLore.some(e => e.id === book.id) : false
    }
    function activateLocally(book: loreBook){
        if(!book.id){
            book.id = v4()
        }
        
        const childLore: loreBook = {
            key: '',
            comment: '',
            content: '',
            mode: 'child',
            insertorder: 100,
            alwaysActive: true,
            secondkey: '',
            selective: false,
            id: book.id,
        }
        getCurrentChat().localLore.push(childLore)
    }
    function deactivateLocally(book: loreBook){
        if(!book.id) return
        const chat = getCurrentChat()
        const childLore = chat?.localLore?.find(e => e.id === book.id)
        if(childLore){
            chat.localLore = chat.localLore.filter(e => e.id !== book.id)
        }
    }
    function toggleLocalActive(check: boolean, book: loreBook){
        if(check){
            activateLocally(book)
        }else{
            deactivateLocally(book)
        }
    }
    function getParentLoreName(book: loreBook){
        if(book.mode === 'child'){
            const value = getCurrentCharacter()?.globalLore.find(e => e.id === book.id)
            if(value){
                return value.comment.length === 0 ? value.key.length === 0 ? "Unnamed Lore" : value.key : value.comment
            }
        }
    }

    function toggleAlwaysActive() {
        if (value.mode === 'folder') {
            for (let i = 0; i < externalLoreBooks.length; i++) {
                if (externalLoreBooks[i].folder === value.key) {
                    externalLoreBooks[i].alwaysActive = !value.alwaysActive
                }
            }
        }
        value.alwaysActive = !value.alwaysActive
    }

    
</script>
<div class={"w-full flex flex-col " + (
    isLastInContainer ? 
        'pb-0 mb-0 border-0' : // Last item in container: no border
        'pb-2 mb-2 border-b border-b-selected last:pb-0 last:mb-0 last:border-0'
)}
    class:lore-row={true}
    class:hidden-entry={visualStatus.hidden}
    class:unreachable-entry={visualStatus.unreachable && !visualStatus.hidden}
    class:no-sort={value.mode === 'folder' && openFolders > 0}
    data-risu-idx={idx} data-risu-idgroup={idgroup}
>
    <div class="flex items-center transition-colors w-full p-1">

    {#if value.mode !== 'child'}
        <button class="endflex valuer border-darkborderc flex items-center" onclick={() => {
            value.secondkey = value.secondkey ?? ''
            if(!open){
                open = true
                onOpen(value.mode !== 'folder') // If not a folder, pass true
            }
            else{
                open = false
                onClose(value.mode !== 'folder') // If not a folder, pass true
            }
        }}>
            {#if value.mode === 'folder'}
                {#if open}
                    <FolderOpen size={20} class="mr-1" />
                {:else}
                    <FolderIcon size={20} class="mr-1" />
                {/if}
            {/if}
            {#if value.mode === 'folder'}
                <span class="lore-row-title">{value.comment.length === 0 ? "Unnamed Folder" : value.comment}</span>
            {:else}
                <span class="lore-row-title">{value.comment.length === 0 ? value.key.length === 0 ? "Unnamed Lore" : value.key : value.comment}</span>
            {/if}
        </button>
        <span class="inline-status"><LoreBookStatusIcons entry={value} size="1.2rem" onActivationClick={toggleAlwaysActive} /></span>
        <button class="inline-delete" data-lorebook-inline-delete aria-label={language.lorebookWorkspace.deleteEntry} title={language.lorebookWorkspace.deleteEntry} onclick={async () => {
            let shouldRemove = true;
            if (value.mode === 'folder' && externalLoreBooks.some(e => e.folder === value.key)) {
                const firstConfirm = await alertConfirm(language.folderRemoveConfirm);
                if (!firstConfirm) {
                    shouldRemove = false;
                }
            }

            if (shouldRemove) {
                const secondConfirm = await alertConfirm(language.removeConfirm + (value.comment || 'Unnamed Folder'));
                if (secondConfirm) {
                    // onRemove in LoreBookList already closes the entry when it is
                    // open; closing a closed one here drove openedDetails negative
                    // and permanently killed drag reordering.
                    deactivateLocally(value);
                    onRemove();
                }
            }
        }}>
            <SolarIcon src={trashIcon} name="trash-bin-trash-bold" size="1.2rem" />
        </button>
    {:else}
        <button class="endflex valuer border-darkborderc" onclick={() => alertMd(language.childLoreDesc)}>
            <BookCopyIcon size={20} class="mr-1" />
            <span class="lore-row-title">{getParentLoreName(value)}</span>
        </button>
        <span class="inline-status"><LoreBookStatusIcons entry={value} size="1.2rem" /></span>
        <button class="inline-delete" data-lorebook-inline-delete aria-label={language.lorebookWorkspace.deleteEntry} title={language.lorebookWorkspace.deleteEntry} onclick={async () => {
            const d = await alertConfirm(language.removeConfirm + getParentLoreName(value))
            if(d){
                onRemove()
            }
        }}>
            <SolarIcon src={trashIcon} name="trash-bin-trash-bold" size="1.2rem" />
        </button>
    {/if}
    </div>
    {#if open}
        {#if value.mode === 'folder'}
        <div class="border-0 outline-hidden w-full mt-2 flex flex-col mb-2">
            <span class="text-textcolor mt-6 mb-2">{language.folderName}</span>
            <TextInput bind:value={value.comment}/>

            <div class="mt-4">
                <LoreBookList externalLoreBooks={externalLoreBooks} showFolder={value.key} />
            </div>
            
            <div class="mt-2 flex gap-1">
                <button class="text-textcolor2 hover:text-textcolor" onclick={() => {
                    externalLoreBooks.push({
                        key: '',
                        comment: '',
                        content: '',
                        mode: 'normal',
                        insertorder: 100,
                        alwaysActive: true,
                        secondkey: '',
                        selective: false,
                        folder: value.key,
                    })
                }}>
                    <PlusIcon size={20} />
                </button>
            </div>
        </div>
        {:else}
        <div class="border-0 outline-hidden w-full mt-2 flex flex-col mb-2">
            <span class="text-textcolor mt-6">{language.name} <Help key="loreName"/></span>
            <TextInput bind:value={value.comment}/>
            {#if !value.alwaysActive}
                <span class="text-textcolor mt-6">{language.activationKeys} <Help key="loreActivationKey"/></span>
                <span class="text-xs text-textcolor2">{language.activationKeysInfo}</span>
                <TextInput bind:value={value.key}/>

                {#if value.selective}
                    <span class="text-textcolor mt-6">{language.SecondaryKeys}</span>
                    <span class="text-xs text-textcolor2">{language.activationKeysInfo}</span>
                    <TextInput bind:value={value.secondkey}/>
                {/if}
            {/if}
            {#if !(value.activationPercent === undefined || value.activationPercent === null)}
                <span class="text-textcolor mt-6">{language.activationProbability}</span>
                <NumberInput bind:value={value.activationPercent} onChange={() => {
                    if(isNaN(value.activationPercent) || !value.activationPercent || value.activationPercent < 0){
                        value.activationPercent = 0
                    }
                    if(value.activationPercent > 100){
                        value.activationPercent = 100
                    }
                }} />
            {/if}
            <span class="text-textcolor mt-4">{language.insertOrder} <Help key="loreorder"/></span>
            <NumberInput bind:value={value.insertorder} min={0} max={1000}/>
            <span class="text-textcolor mt-4 mb-2">{language.prompt}</span>
            <TextAreaInput highlight autocomplete="off" bind:value={value.content} />
            <span class="text-textcolor2 mt-2 mb-2 text-sm">{tokens} {language.tokens}</span>
            <div class="flex items-center mt-4">
                <Check bind:check={value.alwaysActive} name={language.alwaysActive}/>
            </div>
            <div class="flex items-center mt-2" data-lorebook-hidden>
                <Check check={value.enabled === false} onChange={(hidden: boolean) => value.enabled = !hidden} name={language.lorebookWorkspace.hidden}/>
            </div>
            {#if !value.alwaysActive && getCurrentCharacter()?.globalLore?.includes(value) && DBState.db.localActivationInGlobalLorebook}
                <div class="flex items-center mt-2">
                    <Check check={isLocallyActivated(value)} onChange={(check: boolean) => toggleLocalActive(check, value)} name={language.alwaysActiveInChat}/>
                </div>
            {/if}
            {#if !value.useRegex}
                <div class="flex items-center mt-2">
                    <Check bind:check={value.selective} name={language.selective}/>
                    <Help key="loreSelective" name={language.selective}/>
                </div>
            {/if}
            {#if !value.alwaysActive}
                <div class="flex items-center mt-2">
                    <Check bind:check={value.useRegex} name={language.useRegexLorebook}/>
                    <Help key="useRegexLorebook" name={language.useRegexLorebook}/>
                </div>
            {/if}
        </div>
        {/if}
    {/if}
</div>



<style>
    .valuer:hover{
        color: var(--color-success);
        cursor: pointer;
    }

    .endflex{
        display: flex;
        flex-grow: 1;
        cursor: pointer;
    }

    .inline-status { display: inline-flex; align-items: center; margin-right: .22rem; }
    .inline-delete { display: inline-grid; width: 2rem; height: 2rem; flex: 0 0 2rem; padding: 0; place-items: center; border-radius: .38rem; color: var(--color-danger); opacity: 0; pointer-events: none; transition: opacity 140ms ease, background-color 140ms ease; }
    .lore-row:hover .inline-delete, .lore-row:focus-within .inline-delete { opacity: 1; pointer-events: auto; }
    .inline-delete:hover { background: color-mix(in srgb, var(--color-danger) 12%, transparent); }
    .unreachable-entry .lore-row-title { color: var(--color-danger); }
    .hidden-entry .lore-row-title { color: var(--color-textcolor2); opacity: .56; }

    @media (hover: none) {
        .inline-delete { opacity: 1; pointer-events: auto; }
    }

    /* Styles for SortableJS drag-and-drop feedback */
    :global(.risu-chosen-item) {
        /* The item being dragged */
        padding-bottom: 0.5rem;
        margin-bottom: 0.5rem;
        border-bottom: 1px solid;
        border-bottom-color: var(--risu-theme-selected);
        opacity: 0.7;
    }

    :global(.risu-ghost-item) {
        /* The placeholder for the drop location */
        background-color: color-mix(in srgb, var(--color-selected) 20%, transparent);

    }
</style>
